import {effect, inject, Injectable} from "@angular/core";
import {FarmStore} from "src/app/domain/assets/farm/farm.store";
import {FarmApiClient} from "src/app/infrastructure/moovement-api/web-api/farm.api-client";
import {DataStorageService} from "src/app/infrastructure/data-storage.service";
import {DataType} from "src/app/domain/domain-models";
import {Farm} from "src/app/infrastructure/moovement-api/models/farm";
import {catchError, lastValueFrom, Observable, tap} from "rxjs";
import {HttpClient} from "@angular/common/http";
import {WeatherResponse} from "src/app/presentation/common/weather-widget/weather-response";
import {
  InternalNotificationsStore,
  InternalNotificationType
} from "src/app/infrastructure/internal-notifications/internal-notifications.store";
import {PaddockService} from "src/app/domain/assets/paddocks/paddock.service";
import {SynchronizationStatus} from "src/app/infrastructure/moovement-api/models/asset";

@Injectable({
  providedIn: 'root'
})
export class FarmService {

  private farmStore = inject(FarmStore);
  private internalNotificationsStore = inject(InternalNotificationsStore);

  constructor(
    private farmApiClient: FarmApiClient,
    private dataStorageService: DataStorageService,
    private httpClient: HttpClient,
    private paddockService: PaddockService
  ) {
    effect(() => {
      const farm = this.farmStore.farm();
      if (!farm) return;

      this.refreshWeather(farm);
      this.refreshGeometries(farm.id);
    });
  }

  reset() {
    this.farmStore.reset();
  }

  getFarm(farmId: string): void {
    const result$ = this.farmApiClient.getFarm(farmId).pipe(
      tap(async farm => {
        await this.dataStorageService.saveAsset(DataType.farms, farmId, farm, SynchronizationStatus.upToDate);
        this.farmStore.setFarm(farm);
      }),
      catchError(async () => {
        const cachedFarms = await this.dataStorageService.getAllActiveAssetsMatching<Farm>(DataType.farms, farmId);
        if (cachedFarms.length !== 0) this.farmStore.setFarm(cachedFarms[0]);
      })
    ).subscribe();
  }

  updateFarm(farm: Farm): Observable<void | Farm> {
    const updatedFarm$ = this.farmApiClient.updateFarm(farm).pipe(
      tap(async updatedFarm => {
        await this.dataStorageService.saveAsset(DataType.farms, updatedFarm.id, updatedFarm, SynchronizationStatus.upToDate);
        this.farmStore.setFarm(updatedFarm);
        this.internalNotificationsStore.addNotification("Succesfully updated farm.", InternalNotificationType.SUCCESS, false);
      }),
      catchError(async () => {
        this.internalNotificationsStore.addNotification("Failed to update farm.", InternalNotificationType.ERROR, false);
      })
    );

    return updatedFarm$;
  }

  addPaddocks(farmId: string, file: File) {
    this.farmApiClient.addPaddocks(farmId, file).pipe(
      tap(paddocks => {
        this.internalNotificationsStore.addNotification("Successfully created paddocks.", InternalNotificationType.SUCCESS, false);
        this.paddockService.savePaddocks(farmId, paddocks);
        this.refreshGeometries(farmId);
      }),
      catchError(async () => {
        this.internalNotificationsStore.addNotification("Failed to create paddocks.", InternalNotificationType.ERROR, false);
      })
    ).subscribe();
  }

  private refreshWeather(farm: Farm) {
    const location = farm.location?.coords;
    if (location?.length !== 2) return;

    const lat = location[0]
    const lng = location[1]
    const metrics = "temperature_2m,wind_speed_10m,relative_humidity_2m,wind_speed_10m"
    const countryCode = farm.address.countryCode;
    const windSpeedUnit = countryCode === 'US' ? 'mph' : 'kmh';
    const temperatureUnit = countryCode === 'US' ? 'fahrenheit': 'celsius';
    const url = `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lng}&current=${metrics}&wind_speed_unit=${windSpeedUnit}&temperature_unit=${temperatureUnit}`;
    this.httpClient.get<WeatherResponse>(url).subscribe(weatherResponse => {
      this.farmStore.setWeather(weatherResponse);
    });
  }

  private refreshGeometries(farmId: string) {
    this.farmApiClient.getPaddockGeometries(farmId).subscribe(geometries => {
      this.farmStore.setPaddockGeometries(geometries);
    });
  }
}
